import ListItemLoading from "@/components/loading/listItemLoading/listItemLoading";
import { SmsGroupTemplateType, SmsTemplateStatus, SmsType } from "@/models/club";
import BottomSheet from "@/modules/smsLibrary/BottomSheet";
import PageHeader from "@/modules/smsLibrary/PageHeader";
import TemplateCard from "@/modules/smsLibrary/TemplateCard";
import { allSmsTypes, faNumber, previewText, stripOptOut, withOptOut } from "@/modules/smsLibrary/sms";
import classes from "@/modules/smsLibrary/sms.module.scss";
import { SmsTemplate } from "@/services/discount/type";
import { useGetSmsPlaceHolder } from "@/services/smsPlaceHolders/hooks/useGetSmsPlaceHolder";
import useRemoveTemplate from "@/services/template/hooks/useRemoveTemplate";
import useDiscountStore from "@/store/discount";
import useMessageStore from "@/store/message";
import useRegionMessageStore from "@/store/regionMessage";
import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCopy, IconEye, IconInfoCircle, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useGetSmsTemplates from "../../messages/hooks/useGetSmsTemplates";

type Filter = "all" | SmsTemplateStatus;
const filters: { id: Filter; label: string }[] = [
  { id: "all", label: "همه" },
  { id: SmsTemplateStatus.Value1, label: "تأییدشده" },
  { id: SmsTemplateStatus.Value0, label: "در انتظار" },
  { id: SmsTemplateStatus.Value2, label: "ردشده" },
];

function Templates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useGetSmsTemplates(SmsGroupTemplateType.Value4);
  const { data: tags } = useGetSmsPlaceHolder();
  const { mutate: remove, isLoading: removing } = useRemoveTemplate();
  const messageStore = useMessageStore();
  const discountStore = useDiscountStore();
  const regionStore = useRegionMessageStore();
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<SmsTemplate>();
  const [mode, setMode] = useState<"actions" | "preview" | "confirm">("actions");

  const templates = (data ?? []).flatMap((group) => group.templates ?? []);
  const visible = templates.filter((t) => filter === "all" || t.status === filter);
  const countOf = (id: Filter) => (id === "all" ? templates.length : templates.filter((t) => t.status === id).length);

  const openSheet = (template: SmsTemplate) => {
    setSelected(template);
    setMode("actions");
  };
  const closeSheet = () => setSelected(undefined);
  // Replacing the sheet's history entry keeps the back button from reopening it.
  const leaveSheet = (to: string, template: SmsTemplate) => {
    closeSheet();
    navigate(to, { replace: true, state: { template } });
  };
  const edit = (template: SmsTemplate) => navigate(`edit?id=${template.id}`, { state: { template } });

  // Opens the send flow that fits the template, with the template already chosen.
  const send = (template: SmsTemplate) => {
    const types = template.applicableSmsTypes?.length ? template.applicableSmsTypes : allSmsTypes;
    if (types.includes(SmsType.Value0)) {
      messageStore.setTemplate({ smsTemplate: template });
      navigate("/dashboard/messages/selectUser");
    } else if (types.includes(SmsType.Value1)) {
      discountStore.setSmsTemplate({ smsTemplate: template });
      navigate("/dashboard/discount/selectGroup");
    } else {
      regionStore.setTemplate({ smsTemplate: template });
      navigate("/dashboard/areaMessage/selectArea");
    }
  };

  const confirmRemove = () => {
    if (!selected?.id) return;
    remove(selected.id, {
      onSuccess: () => {
        notifications.show({ color: "green", message: "قالب حذف شد" });
        queryClient.invalidateQueries({ queryKey: ["allSmsTemplate"] });
        // Going back drops the sheet's #hash entry; the drawer then closes itself.
        if (window.location.hash === "#templateActions") window.history.back();
        else closeSheet();
      },
    });
  };

  return (
    <div className={classes.page}>
      <PageHeader title="قالب‌های من" />
      <div className={classes.body}>
        <p className={classes.intro}>متن‌های آماده‌ی خودتان برای ارسال سریع پیامک. هر قالب بعد از تأیید پدیکس در ارسال‌ها قابل انتخاب است.</p>

        {templates.length > 0 && (
          <div className={classes.filters} role="group" aria-label="فیلتر وضعیت">
            {filters.map((f) => (
              <button type="button" key={f.id} className={classes.filterChip} aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
                {f.label}
                <span className={classes.filterCount}>{faNumber(countOf(f.id))}</span>
              </button>
            ))}
          </div>
        )}

        <div className={classes.notice}>
          <IconInfoCircle size={19} stroke={1.8} aria-hidden />
          <span>فقط قالب‌های تأییدشده در ارسال پیامک نمایش داده می‌شوند. ویرایش قالب، آن را دوباره به صف بررسی می‌فرستد.</span>
        </div>

        {isLoading ? (
          <ListItemLoading count={3} />
        ) : templates.length === 0 ? (
          <div className={classes.empty}>
            <strong>هنوز قالبی نساخته‌اید</strong>
            متن‌هایی را که زیاد می‌فرستید یک بار بنویسید و هر بار با یک لمس استفاده کنید.
          </div>
        ) : visible.length === 0 ? (
          <div className={classes.empty}>قالبی با این وضعیت ندارید.</div>
        ) : (
          visible.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              tags={tags ?? []}
              onMenu={() => openSheet(template)}
              onEdit={() => edit(template)}
              onSend={() => send(template)}
            />
          ))
        )}
      </div>

      <div className={classes.stickyBar}>
        <Button fullWidth h={52} radius={14} color="myColor.7" fz={15} fw={800} leftSection={<IconPlus size={20} stroke={2.2} />} onClick={() => navigate("add")}>
          قالب جدید
        </Button>
      </div>

      <BottomSheet opened={!!selected} onClose={closeSheet} name="templateActions" title={selected?.title}>
        {mode === "actions" && (
          <div className={classes.sheetActions}>
            <button type="button" className={classes.sheetAction} onClick={() => setMode("preview")}>
              <IconEye size={21} stroke={1.8} aria-hidden />
              پیش‌نمایش پیامک
            </button>
            <button type="button" className={classes.sheetAction} onClick={() => selected && leaveSheet(`edit?id=${selected.id}`, selected)}>
              <IconPencil size={21} stroke={1.8} aria-hidden />
              <span>
                ویرایش
                {selected?.status !== SmsTemplateStatus.Value0 && <span className={classes.sheetHint}>بعد از ذخیره، دوباره بررسی می‌شود</span>}
              </span>
            </button>
            <button type="button" className={classes.sheetAction} onClick={() => selected && leaveSheet("add", selected)}>
              <IconCopy size={21} stroke={1.8} aria-hidden />
              <span>
                ساخت نسخه‌ی کپی
                <span className={classes.sheetHint}>قالب فعلی دست نمی‌خورد</span>
              </span>
            </button>
            <button type="button" className={classes.sheetAction} data-danger="true" onClick={() => setMode("confirm")}>
              <IconTrash size={21} stroke={1.8} aria-hidden />
              حذف قالب
            </button>
          </div>
        )}
        {mode === "preview" && (
          <div className={classes.previewStage}>
            <p className={classes.bubble}>{withOptOut(previewText(stripOptOut(selected?.text), tags ?? []))}</p>
          </div>
        )}
        {mode === "confirm" && (
          <div className={classes.noticeText} style={{ gap: 16 }}>
            <span style={{ fontSize: 14, lineHeight: 1.9 }}>«{selected?.title}» حذف شود؟ این کار برگشت‌پذیر نیست.</span>
            <div className={classes.summaryActions}>
              <Button flex={1} h={48} radius={12} color="red" loading={removing} onClick={confirmRemove}>
                حذف
              </Button>
              <Button flex={1} h={48} radius={12} variant="default" onClick={() => setMode("actions")}>
                انصراف
              </Button>
            </div>
          </div>
        )}
      </BottomSheet>
    </div>
  );
}

export default Templates;
